import React, { useState } from "react";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { HeaderHelp } from "../AdminHeaderHelp";

// Styles, formatters, form parts and table sorting shared by Pool Protection,
// Pool Setup and Profit Mint.
// Styles are inline: the admin page loads this pack's JS but not its CSS.

export const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
export const h2 = { fontSize: "16px", margin: "0 0 6px" };
export const note = { fontSize: "13px", color: colors.gray600, margin: "0 0 12px" };
export const help = { fontSize: "12px", color: colors.gray600, marginTop: "4px" };
export const label = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" };
export const input = {
  width: "100%", maxWidth: "220px", padding: "7px 10px", fontSize: "14px",
  border: `1px solid ${colors.gray400}`, borderRadius: "6px", boxSizing: "border-box",
};
// Header cells stay in view while the table scrolls inside its box.
export const th = {
  position: "sticky", top: 0, zIndex: 1, background: colors.white,
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap",
};
export const thNum = { ...th, textAlign: "right" };
// A header button that sorts its table; it looks like the header text.
export const sortBtn = {
  background: "none", border: 0, padding: 0, font: "inherit", color: "inherit",
  textTransform: "inherit", letterSpacing: "inherit", cursor: "pointer",
};
export const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
export const tdNum = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
export const mono = { fontFamily: "monospace", fontSize: "12px" };
// The scroll box for a table, so its sticky header has something to stick to.
export const tableBox = (maxHeight = "70vh") => ({ overflow: "auto", maxHeight });
export const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "16px" };
export const btn = (disabled) => ({
  background: colors.primary, color: "#fff", border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
export const ghostBtn = (disabled) => ({ ...btn(disabled), background: "#fff", color: colors.primary });
export const dangerBtn = (disabled) => ({ ...btn(disabled), background: colors.danger, borderColor: colors.danger });
export const linkBtn = {
  background: "none", border: "none", padding: 0, color: colors.primary, cursor: "pointer",
  fontSize: "13px", textDecoration: "underline", marginLeft: "10px",
};
export const TONES = {
  good: { bg: "#d4edda", fg: "#155724" },
  warn: { bg: "#fff3cd", fg: "#856404" },
  bad: { bg: "#f8d7da", fg: "#721c24" },
  info: { bg: "#d1ecf1", fg: "#0c5460" },
  neutral: { bg: "#e9ecef", fg: "#495057" },
};
export const Tag = ({ tone, children }) => (
  <span style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
    {children}
  </span>
);
export const Banner = ({ tone, children }) => (
  <div role="status" style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px" }}>
    {children}
  </div>
);

export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const amount = (v) => num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
export const sentence = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "");
export const percent = (f) => `${(num(f) * 100).toFixed(1)}%`;
// A signed number that rounds to zero reads "0.00" in grey, never "-0.00" in red.
const roundsToZero = (v, places = 2) => Math.round(num(v) * 10 ** places) === 0;
export const signed = (v) => (roundsToZero(v) ? "0.00" : `${num(v) > 0 ? "+" : ""}${amount(v)}`);
export const signedTone = (v, places = 2) => ({
  color: roundsToZero(v, places) ? colors.gray600 : num(v) < 0 ? colors.danger : "#155724",
});
// A share that can go below zero, as a percent: "-0.4%", or "0.0%" when it rounds to zero.
export const signedPercent = (f) => (roundsToZero(f, 3) ? "0.0%" : percent(f));

// "0.0.1@171.2" becomes "0.0.1-171-2", the form HashScan links take (as in Treasury::Explorer).
export const hashscanTx = (network, id) =>
  `https://hashscan.io/${network === "testnet" ? "testnet" : "mainnet"}/transaction/${String(id).replace(/@(\d+)\.(\d+)$/, "-$1-$2")}`;
// A transaction id short enough for a table cell: its first and last characters.
export const shortTx = (id) => {
  const s = String(id || "");
  return s.length > 20 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;
};

// Pools a dry run says can be put at 100%.
export const readyToOpen = (report) => (report?.pools || []).filter((r) => !r.skipped && !r.opened);

// Pools a split dry run can save.
export const readyToSplit = (report) => (report?.pools || []).filter((r) => !r.skipped);

export const Field = ({ id, title, hint, suffix, ...rest }) => (
  <div>
    <label style={label} htmlFor={id}>{title}</label>
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <input id={id} style={input} {...rest} />
      {suffix && <span style={{ fontSize: "13px" }}>{suffix}</span>}
    </div>
    {hint && <div style={help}>{hint}</div>}
  </div>
);

export const ReasonField = ({ id, value, onChange }) => (
  <Field id={id} title="Reason" type="text" maxLength={500}
    value={value} onChange={(e) => onChange(e.target.value)} style={{ ...input, maxWidth: "460px" }} />
);

// The old staging token set (BC-ALL and the like). lp-api's reconciliation
// leaves them out, and so do the pool tables.
export const isLegacyPool = (p) => /^BC-/i.test(String(p.currencySymbol || ""));

// Sorting a table by its headers. `columns` maps a key to { text, value }: text
// columns sort A to Z on the first click, numbers largest first, and a second
// click reverses. A missing value (a "-" in the table) always goes last, and
// ties sort by currency code.
const missing = (v, text) => v == null || v === "" || (!text && !Number.isFinite(Number(v)));
const byCode = (a, b) => String(a.currencySymbol || "").localeCompare(String(b.currencySymbol || ""));
export const sortRows = (rows, sort, columns) => {
  if (!sort) return rows;
  const { text, value } = columns[sort.key];
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    const ma = missing(va, text);
    const mb = missing(vb, text);
    if (ma || mb) return ma && mb ? byCode(a, b) : ma ? 1 : -1;
    const cmp = text ? String(va).localeCompare(String(vb)) : Number(va) - Number(vb);
    return cmp * dir || byCode(a, b);
  });
};

// The sort state for one table, starting at `initial`, and a header cell that
// sorts it, with an optional "?" that explains the column.
export const useSort = (columns, initial = null) => {
  const [sort, setSort] = useState(initial);
  const sortBy = (key) => setSort((s) => (s?.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: columns[key].text ? "asc" : "desc" }));
  const sortTh = (key, title, numeric, help) => {
    const active = sort?.key === key;
    return (
      <th key={key} style={numeric ? thNum : th} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" style={sortBtn} onClick={() => sortBy(key)}>
          {title}{active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
        </button>
        {help && <HeaderHelp label={title} text={help} />}
      </th>
    );
  };
  return { sort, sortTh };
};
